import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../auth/context/AuthContext';
import VoucherPurchaseForm from '../../vouchers/components/VoucherPurchaseForm';
import VoucherShareCard from '../../vouchers/components/VoucherShareCard';
import { useVoucherShare } from '../../vouchers/hooks/useVoucherShare';
import { issueVoucher } from '../../vouchers/services/voucherService';

const fadeSlide = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
};

export default function AdminVoucherCreatePage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const {
    cardRef,
    copyState,
    capturing,
    copyText,
    captureCardAsImage,
    buildWhatsAppHref,
    buildMailtoHref,
    buildVoucherShareText,
  } = useVoucherShare();

  async function handleCreate(payload) {
    setLoading(true);
    setError('');

    try {
      const response = await issueVoucher({
        ...payload,
        performedByUserId: user?.id || null,
        performedByName: user?.name || user?.email || 'Operador',
      });
      setResult(response);
    } catch (requestError) {
      setError('No se pudo generar el bono.');
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setResult(null);
    setError('');
  }

  const whatsappHref = result ? buildWhatsAppHref(result) : null;
  const mailtoHref = result ? buildMailtoHref(result) : null;

  return (
    <div className="space-y-6">
      {/* Header — always visible */}
      <div className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] px-8 py-8 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.32em] text-slate-400">
              {result ? 'Resultado' : 'Crear bono'}
            </p>
            <h2 className="mt-3 text-[56px] leading-[0.94] font-semibold tracking-[-0.06em] text-slate-950">
              {result ? (result.operationType === 'ACCUMULATED' ? 'Horas acumuladas' : 'Bono generado') : 'Venta de bono'}
            </h2>
            <p className="mt-4 text-lg text-slate-500">
              {result
                ? 'Comparte el bono con el cliente por WhatsApp, correo o descarga la imagen.'
                : 'Crea un bono nuevo con nombre, teléfono y horas. El bono se puede compartir inmediatamente.'}
            </p>
          </div>
          {result && (
            <motion.button
              type="button"
              onClick={handleReset}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="self-start rounded-[22px] border-2 border-[var(--app-accent)] text-[var(--app-accent)] hover:bg-[var(--app-accent)] hover:text-white px-6 py-4 font-semibold transition whitespace-nowrap"
            >
              ➕ Crear otro bono
            </motion.button>
          )}
        </div>
      </div>

      {error && (
        <motion.p {...fadeSlide} className="text-red-600 font-semibold">
          {error}
        </motion.p>
      )}

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30, transition: { duration: 0.25 } }}
            transition={{ duration: 0.4 }}
          >
            <VoucherPurchaseForm onCreate={handleCreate} busy={loading} />
          </motion.div>
        ) : (
          <motion.div
            key="result"
            variants={stagger}
            initial="initial"
            animate="animate"
            className="space-y-6"
          >
            <motion.div variants={fadeSlide}>
              <VoucherShareCard voucher={result} cardRef={cardRef} />
            </motion.div>

            <motion.div variants={fadeSlide} className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => copyText(result)}
                className="rounded-[20px] bg-[#0a84ff] hover:bg-[#0077ed] text-white px-5 py-3 font-medium transition"
              >
                {copyState === 'copied' ? '✓ Copiado' : copyState === 'error' ? 'Error al copiar' : 'Copiar resumen'}
              </button>
              <button
                type="button"
                onClick={() => captureCardAsImage(result)}
                disabled={capturing}
                className="rounded-[20px] bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 font-medium transition disabled:opacity-60"
              >
                {capturing ? 'Generando...' : '📸 Descargar imagen'}
              </button>
              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-[20px] bg-[#25D366] hover:bg-[#20ba58] text-white px-5 py-3 font-medium transition"
                >
                  Enviar por WhatsApp
                </a>
              )}
              <a
                href={mailtoHref || '#'}
                className={`rounded-[20px] px-5 py-3 font-medium transition ${mailtoHref ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 text-slate-400 pointer-events-none'}`}
              >
                Enviar por correo
              </a>
            </motion.div>

            <motion.details variants={fadeSlide} className="rounded-[24px] border border-[var(--app-border)] bg-[rgba(245,245,247,0.85)] overflow-hidden">
              <summary className="px-5 py-4 cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-900 transition select-none">
                Ver resumen de texto para compartir
              </summary>
              <pre className="px-5 pb-5 whitespace-pre-wrap font-sans text-sm leading-6 text-slate-700">{buildVoucherShareText(result)}</pre>
            </motion.details>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
